import React, { useState } from "react";
import Papa from "papaparse";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Input,
  InputLabel,
  CircularProgress,
  Collapse,
  Typography,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: theme.spacing(0),
    paddingBottom: theme.spacing(0),
  },
  fileName: {
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  button: {
    marginRight: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  list: {
    padding: theme.spacing(0),
    margin: theme.spacing(0, 3, 1),
  },
  error: {
    padding: theme.spacing(2, 2, 0),
  },
}));

const OtaCsvUpload = ({ isOpen, onClose, onUpload }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const [file, setFile] = useState();
  const [text, setText] = useState();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const reader = new FileReader();
  const allowedExtensions = ["csv"];

  const handleOnChange = (e) => {
    setError("");
    if (e.target.files.length) {
      const inputFile = e.target.files[0];
      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        const errorMessage = `${t("OTA.VehicleSearch.CsvUploadModal.UploadError")}: ${t(
          "OTA.VehicleSearch.CsvUploadModal.NotCsv",
        )}`;
        setError(errorMessage);
        return;
      }
      setFile(inputFile);
    }
  };

  const csvFileToArray = (string) => {
    const results = Papa.parse(string, { header: true });
    const fields = ["shortcode", "Shortcode"];
    const headers = results.meta.fields;

    if (!headers || !headers.some((h) => fields.includes(h))) {
      const errorMessage = `${t("OTA.VehicleSearch.CsvUploadModal.UploadError")}: ${t(
        "OTA.VehicleSearch.CsvUploadModal.MissingShortcodeHeader",
      )}`;
      setError(errorMessage);
      return;
    }
    const rows = results.data;
    const shortcodes = rows
      .map(
        (vehicle) =>
          // eslint-disable-next-line no-nested-ternary
          vehicle.shortcode ? vehicle.shortcode : vehicle.Shortcode ? vehicle.Shortcode : null,
      )
      .filter((vehicle) => vehicle);
    if (!shortcodes.length) {
      const errorMessage = `${t("OTA.VehicleSearch.CsvUploadModal.UploadError")}: ${t(
        "OTA.VehicleSearch.CsvUploadModal.MissingShortcodes",
      )}`;
      setError(errorMessage);
      return;
    }
    const shortcodesString = shortcodes.toString();

    setText(shortcodesString);
    // eslint-disable-next-line consistent-return
    return shortcodesString;
  };

  const handleSubmit = async (e) => {
    let csvString = "";
    setLoading(true);
    if (e) e.preventDefault();

    try {
      if (file) {
        reader.onload = (event) => {
          const fileContent = event.target.result;
          csvString = csvFileToArray(fileContent);
          if (text && text === csvString) onUpload(text);
          else onUpload(csvString);
        };
        reader.readAsText(file);
      }
    } catch (err) {
      console.error(err);
      const errorMessage = `${t("OTA.VehicleSearch.CsvUploadModal.UploadError")}: ${
        err.message || err
      }`;
      setError(errorMessage);
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle className={classes.title}>
        {t("OTA.VehicleSearch.CsvUploadModal.Title")}
      </DialogTitle>
      <DialogContent style={{ maxWidth: "400px", marginTop: 0 }}>
        <DialogContentText component="div">
          <ul className={classes.list}>
            <li>{t("OTA.VehicleSearch.CsvUploadModal.Instructions1")}</li>
            <li>{t("OTA.VehicleSearch.CsvUploadModal.Instructions2")}</li>
          </ul>
        </DialogContentText>
        <Grid>
          <Input
            type="file"
            id="uploadFile"
            data-testid="upload-file"
            inputProps={{ accept: ".csv" }}
            onChange={handleOnChange}
            style={{ display: "none" }}
          />
          <InputLabel htmlFor="uploadFile">
            <Button fullWidth color="primary" variant="contained" component="span">
              {t("OTA.VehicleSearch.CsvUploadModal.Import")}
            </Button>
          </InputLabel>
        </Grid>
        <Typography className={classes.fileName}>
          {file
            ? t("OTA.VehicleSearch.CsvUploadModal.File")
            : t("OTA.VehicleSearch.CsvUploadModal.NoFile")}
          {file && `: ${file?.name}`}
        </Typography>
        <Collapse in={loading} timeout={1000}>
          <CircularProgress color="secondary" />
        </Collapse>
        <Collapse in={!!error} timeout={1000}>
          <Typography color="error" className={classes.error}>
            {error}
          </Typography>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <DialogActions>
          <Button onClick={onClose} className={classes.button}>
            {t("OTA.VehicleSearch.CsvUploadModal.Cancel")}
          </Button>
          <Button onClick={handleSubmit} className={classes.button}>
            {t("OTA.VehicleSearch.CsvUploadModal.Confirm")}
          </Button>
        </DialogActions>
      </DialogActions>
    </Dialog>
  );
};

OtaCsvUpload.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

export default OtaCsvUpload;
